import CommentResp from "@/interface/api/CommentResp";
import restAccess from "@/rest/restAccess";


export default class CommentManager{
    private loader:CommentLoader;
    private callBacker:CallBaker
    private initialStartTime:number|null = null;
    private commentStocks = new Map<string, CommentStock>()//条件別のコメントのストックを保有する

    constructor(projectId:number, publicPageId:number, callback:(comment:CommentResp)=>void){
        this.loader = new CommentLoader(projectId, publicPageId);
        this.callBacker = new CallBaker(callback)
        const stock = new CommentStock(this.loader, null)
        this.commentStocks.set("", stock)

        //初期条件のコメントを先に読込開始しておく
        stock.loadIfNecessary().then(()=>{
            if(this.initialStartTime){
                this.callBacker.startCallBack(this.initialStartTime)
            }
        })
    }

    // 指定した条件のコメントをロードして内部に保有する。コメント条件別に保有し、表示タイミングになったら、対象コメントをコールバックで通知する。
    // コメントのストックが少なくなったら自動的に続きを読み込む。
    public switchCondition(conditionJson:string, startTime:number) {
        const stock = this.commentStocks.get(conditionJson)
        
        if(stock){
            this.callBacker.switchStock(stock, startTime)
            if(stock.isLoaded()){
                this.callBacker.startCallBack(startTime)
            }else{
                this.initialStartTime = startTime
            }
        }else{
            const stock = new CommentStock(this.loader, conditionJson, startTime)
            this.commentStocks.set(conditionJson, stock)
            this.callBacker.switchStock(stock, startTime)
            stock.loadIfNecessary().then(()=>{
                this.callBacker.startCallBack(startTime)
            })
        }
    }


    //先に読み込んでおいたコメントが他のページに移動しても読み込まれたりコールバックされるのを防ぐ
    public destroy(){
        this.loader.destroy();
        this.callBacker.destroy();
    }

}

const LOAD_SPAN = 1000//ミリ秒。条件毎のstock作成については間隔を開ける

class CommentLoader{
    private projectId:number;
    private publicPageId:number;
    private waitCount = 0;
    private prevTime:null|Date = null
    private isDestroyed = false

    constructor(projectId:number, publicPageId:number){
        this.projectId = projectId;
        this.publicPageId = publicPageId
    }
    public destroy(){
        this.isDestroyed = true;
    }

    //コンテンツ（FM）の作りによっては、次々と異なる条件のコメント読込要求が来る可能性がある。
    //これに対応するため、間隔を開けて読込を行う。待ち数を記録して、間隔を開けてロードする。
    public load(loadCondition:string|null, lastComment:CommentResp|null):Promise<CommentResp[]> {
        if(this.prevTime === null){
            this.prevTime = new Date()
            return this.loadWithGet(loadCondition, lastComment)
        }

        const diff = (new Date()).getTime() - (this.prevTime).getTime()
        if(diff >= LOAD_SPAN && this.waitCount === 0){
            this.prevTime = new Date()
            return this.loadWithGet(loadCondition, lastComment)
        }

        const timeout = LOAD_SPAN - diff + this.waitCount * LOAD_SPAN
        ++this.waitCount

        return new Promise((resolve)=>{
            setTimeout(()=>{
                --this.waitCount;
                this.loadWithGet(loadCondition, lastComment).then((ret)=>{
                    if(!this.isDestroyed){
                        resolve(ret)
                    }
                })
            }, timeout)    
        })    
    }

    private loadWithGet(loadCondition:string|null, lastComment:CommentResp|null):Promise<CommentResp[]>{
        if(this.isDestroyed){
           return new Promise(()=>[]);
        }
        return restAccess.getCommentsPublicPage(
            this.projectId,
            this.publicPageId, 
            loadCondition, 
            lastComment,
            process.env.VUE_APP_MODE === "development" ? 50: 100
        )
    }

}

class CallBaker{    
    private clearCallBackId:number = -1
    private callBackFunc:null|((comment:CommentResp)=>void);
    private stock!:CommentStock;
    private isDestroyed = false

    constructor(callBackFunc:(comment:CommentResp)=>void){
        this.callBackFunc = callBackFunc
    }
    public destroy(){
        this.isDestroyed = true;
    }

    public switchStock(newStock:CommentStock, stopTime:number){
        //古いコールバックが設定されていた場合、古い方を止める。
        if(this.stock && this.stock !== newStock){
            this.stopOldCallBack()
            this.stock.stopTime(stopTime)
        }
        this.stock = newStock
    }
    
    public startCallBack(startTime:number){
        this.stock.startTime(startTime)
        this.setNextCallBack()
    }    

    public setNextCallBack(){
        this.stopOldCallBack()

        //もうコメントが残ってない場合はコールバック自体を終了する
        const nextTimeOut = this.stock.getNextTimeOut()

        if(nextTimeOut < 0){
            return;
        }

        this.clearCallBackId = setTimeout(()=>{
            this.clearCallBackId = -1
            this.executeCallBack()
        }, nextTimeOut)
    }

    private stopOldCallBack(){
        if(this.clearCallBackId >= 0){
            clearTimeout(this.clearCallBackId)
            this.clearCallBackId = -1
        }
    }

    private executeCallBack(){
        if(this.isDestroyed){
            return
        }
        
        if(this.callBackFunc){
            this.callBackFunc(this.stock.getComment())
        }
        this.stock.next()

        this.stock.loadIfNecessary().then(()=>{
            this.setNextCallBack()
        })
    }

}

//条件別のコメントをストックするクラス
class CommentStock{
    constructor(
        private loader:CommentLoader,
        private condition: string|null,
        private startedTime: number|null = null,
        private stopedTime:number|null = null,
        private comments: CommentResp[] = [],
        private index: number = 0,
        private status: "none" | "nowLoading" | "allLoaded" = "none"
    ){
    }

    public isLoaded(){
        return this.comments.length > 0 
    }

    public next(){
        ++this.index
    }

    private shouldLoadComment(){
        return this.status === "none" && this.index + 2 >= this.comments.length
    }
    
    private getLastComment():CommentResp{
        return this.comments[this.comments.length - 1]
    }

    public getComment(){
        return this.comments[this.index]
    }
    
    public getNextTimeOut(){
        if(this.comments[this.index]){
            const diff = this.comments[this.index].sceneMilliSecond - this.getCurrentSceneTime()

            //もし現在時間がコメント時間を超えていたら、0秒後に次のコメントを表示する
            return diff < 0 ? 0 : diff
        }else{
            return -1
        }
    }

    private getCurrentSceneTime(){
        return (new Date()).getTime() - this.startedTime!
    }

    public stopTime(time:number){
        this.stopedTime = time
    }
    
    public startTime(time:number){
        if(this.startedTime){
            if(this.stopedTime){
                const diff = this.stopedTime - this.startedTime
                this.startedTime = time - diff
                this.stopedTime = null
            }
        }else{
            this.startedTime = time
        }
    }


    public loadIfNecessary():Promise<void>{
        if(this.shouldLoadComment()){
            this.status = "nowLoading";

            return this.loader.load(this.condition, this.getLastComment()).then((res: CommentResp[]) => {
                this.pushComments(res)           
            });
        }else{
            return new Promise((resolve)=>{resolve()})
        }
    }

    private pushComments(loadedComments: CommentResp[]){
        const comments = this.removeSameComment(loadedComments)
        if(comments.length === 0){
            this.status = "allLoaded";
        }else{
            this.status = "none"
            this.comments.push(...comments)
        }
    }

    private removeSameComment(newComments: CommentResp[]){
        //2回目以降のコメント読込は、最後のコメント時間以降（同一時間を含む）を読込むため、同一時間のコメントが、追加前コメント、ロードコメントの両方に含まれる。
        //このためその重複コメントを検知して削除する。
        //同一時間より後（>）、ではなく同一時間以降（>=）とするのは、読込の切れ目の同一時間に複数のコメントがあった場合に対処するため。
        //skip（読み込んだコメント数から次のコメントのindexを渡す）でやれば、こんな面倒なことしなくていいじゃないか…と思われるかも知れないが、
        //コメント過多な状況になった場合、いずれコメント間引きロジックが必要になる。
        //間引きは（Spring boot側ではなく）DB側で行うのが最も負荷効率が良い。DB側でコメント間引きをすると、間引き量の取得が容易ではない（推定skip量 < 実際skip量、になった場合の検知が簡単ではない）。
        //なので、追加コメントから同一コメントを破棄するというややこしいことを行う。

        //また重複コメントの検出は、フルでループを回すとパフォーマンス上の懸念が若干ある（追加前コメント1000件 x 追加コメント100件 = 100,000ループ）
        //このため２つの配列がsceneMilliSecond順になっていることを利用した打ち切り処理を入れている。
        const lastComment = this.getLastComment()
        if(!lastComment){
            return newComments;
        }
        
        for(let i = 0; i < newComments.length; ++i){
            const newComment = newComments[i]
            //２つの配列はsceneMilliSecond順に並んでいる。時間が一致しなればそれ以降には同一コメントはないのでループを抜ける
            if(newComment.sceneMilliSecond > lastComment.sceneMilliSecond){
                break;
            }

            for(let j = this.comments.length - 1; j >= 0; --j){
                const oldComment = this.comments[j]
                if(newComment.sceneMilliSecond > oldComment.sceneMilliSecond){
                    break;
                }
                //重複コメントがあった場合
                if(newComment.commentId === oldComment.commentId){
                    newComments.splice(i, 1)
                    --i;
                    break;
                }
            }
        }
        return newComments;
    }
}